import { useEffect, useState, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Loader2, Sparkles } from 'lucide-react';

const ProtectedRoute = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isAuthenticated, loading } = useAuth();

  // Determine if we should skip loading based on location state
  const skipLoading = useMemo(() => {
    return location.state?.user != null;
  }, [location.state?.user]);

  useEffect(() => {
    // Only run navigation logic after loading is complete
    if (loading) return;

    if (!isAuthenticated) {
      navigate('/login', { replace: true });
    } else if (user?.needs_onboarding) {
      navigate('/onboarding', { replace: true });
    }
  }, [isAuthenticated, loading, navigate, user?.needs_onboarding]);

  // Show loading while checking auth (unless we have user from state)
  if (loading && !skipLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center">
        <div className="text-center">
          <Sparkles className="w-12 h-12 text-amber-500 mx-auto mb-4 animate-pulse" />
          <Loader2 className="w-8 h-8 text-amber-500 mx-auto animate-spin mb-4" />
          <p className="text-white text-lg">Loading...</p>
        </div>
      </div>
    );
  }

  // If not authenticated after loading, don't render children (will redirect in useEffect)
  if (!isAuthenticated && !loading) {
    return null;
  }

  return children;
};

export default ProtectedRoute;
